import {yupResolver} from '@hookform/resolvers/yup'
import {Bed, BoundingBox} from '@phosphor-icons/react'
import clsx from 'clsx'
import {FC, useEffect, useState} from 'react'
import {useForm, useWatch} from 'react-hook-form'
import Swal from 'sweetalert2'
import {Select} from '../../../../../../components/UI/Select'
import {useTap} from '../../../../../../hooks/useTap'
import {ShowSnackBar} from '../../../../../../util/functions'
import {useCentralServices} from '../../../../../hoteleria/hooks/useCentralServices'
import {useCampaments} from '../../../../hooks/central/useCampaments'
import {useFloors} from '../../../../hooks/hoteleria/useFloors'
import {useModules} from '../../../../hooks/hoteleria/useModules'
import {CensoFiltroResponse, CensusFilterModel} from '../../../../model/census.model'
import {MasterModel} from '../../../../model/response.model'
import {censusSchema} from '../../../../schema/census'
import {CENSUS_SERVICES} from '../../../../services/census.service'
import style from './index.module.scss'

interface Props {
  prevStep: () => void
  nextStep: () => void
  stepper: any
  goToFirstStep: () => void
  goToStep: (index: number) => void
}

export const CensusStep: FC<Props> = ({prevStep, nextStep, stepper, goToFirstStep, goToStep}) => {
  const {
    register,
    setValue,
    watch,
    control,
    formState: {errors},
    setError,
    handleSubmit
  } = useForm<CensusFilterModel>({
    resolver: yupResolver(censusSchema),
    defaultValues: {
      CampamentoId: '',
      TipoEdificacionId: 'NZbfQ7m6o8IpM2GsI9kiY*T*4LHBe9NE',
      ModuloId: '',
      PisoId: '',
    },
  })

  const census = useWatch({name: 'census'})
  const {getCampaments, campaments, clearCampaments} = useCampaments()
  const {getModulesData, modules, clearModules} = useModules()
  const {floors, getFloors, clearFloors} = useFloors()
  const [searching, setSearching] = useState(false)
  const [typeBuildings, setTypeBuildings] = useState<MasterModel[] | any>([])
  const {getMasterTable} = useCentralServices()
  const [loading, setLoading] = useState<boolean>(false)
  const [loadingSave, setLoadingSave] = useState<boolean>(false)
  const [rooms, setRooms] = useState<CensoFiltroResponse[]>([])
  const [initialRooms, setInitialRooms] = useState<CensoFiltroResponse[]>([])
  const [idCenso, setIdCenso] = useState<string>(null)
  const [estadoCenso, setEstadoCenso] = useState<string>(null)
  const [summary, setSummary] = useState({
    CamasTotales: 0,
    CamasTendidas: 0,
    CamasDestendidas: 0,
    CamasPendientes: 0,
  })

  const handleTap = useTap()

  const hasChanges = JSON.stringify(rooms) !== JSON.stringify(initialRooms)

  const handleNext = () => {
    goToStep(3) // Navegar a la pestaña de Programación (step 3, índice 2)
  }

  const handlePrev = () => {
    prevStep()
  }

  useEffect(() => {
    getCampaments({isCombo: true}, 1)
    getTypeBuildings()
    return () => {
      clearCampaments()
      clearModules()
      clearFloors()
    }
  }, [])

  useEffect(() => {
    if(campaments.length === 1) {
      handleCampamentChange(campaments[0])
    }
  }, [campaments])

  const updateRoomBeds = (roomId: string, Estado: string) => {
    setRooms((prev) =>
      prev.map((room) =>
        room.HabitacionId === roomId
          ? {
              ...room,
              Detalle: room.Detalle.map((bed) => ({
                ...bed,
                Estado,
              })),
            }
          : room,
      ),
    )
  }

  const updateBed = (roomId: string, bedId: string, Estado: string) => {
    setRooms((prev) =>
      prev.map((room) =>
        room.HabitacionId === roomId
          ? {
              ...room,
              Detalle: room.Detalle.map((bed) => (bed.CamaId === bedId ? {...bed, Estado} : bed)),
            }
          : room,
      ),
    )
  }

  const getBedColor = (status: string) => {
    switch (status) {
      case 'TEN':
        return style.bedTendido
      case 'DES':
        return style.bedDestendido
      default:
        return style.bedUnknown
    }
  }

  const handleSave = async (Finalizar: boolean) => {
    setLoadingSave(true)
    try {
      const request = {
        CensoCabeceraId: idCenso,
        Finalizar,
        Detalle: rooms.flatMap((r) =>
          r.Detalle.map((d) => ({
            Id: d.CensoDetalleId,
            Estado: d.Estado,
          })),
        ),
      }
      const {data: response} = await CENSUS_SERVICES.saveCensus(request)
      if (response.error) {
        ShowSnackBar('Hubo un error, vuelve a intentarlo', 'error')
      } else {
        ShowSnackBar('Cambios guardados satisfactoriamente')
        const filters: CensusFilterModel = {
          CampamentoId: watch('CampamentoId'),
          TipoEdificacionId: watch('TipoEdificacionId'),
          ModuloId: watch('ModuloId'),
          PisoId: watch('PisoId'),
        }
        const {data: response} = await CENSUS_SERVICES.filterCensus(filters)
        const parts = response.message.split('|');
        const idCenso = parts[0] || '';
        const estado = parts[1] || '';

        if (!response.error && response.data) {
          console.log('data => ', response.data)
          await handleResumen(idCenso)
          setIdCenso(idCenso)
          setEstadoCenso(estado)
          setRooms(response.data)
          setInitialRooms(JSON.parse(JSON.stringify(response.data))) // copia profunda
        } else {
          setIdCenso(idCenso)
          setEstadoCenso(estado)
          setRooms(response.data)
          setInitialRooms(JSON.parse(JSON.stringify(response.data))) // copia profunda
        }
      }
    } catch (error) {
      console.error('Error al guardar:', error)
    } finally {
      setLoadingSave(false)
    }
  }

  const handleCampamentChange = (val: any) => {
    let value = val ? val.value : ''
    setValue('CampamentoId', value)
    setError('CampamentoId', {})
    setValue('ModuloId', '')
    setError('ModuloId', {})
    clearModules()
    setValue('PisoId', '')
    setError('PisoId', {})
    clearFloors()
    getModulesData({CampamentoId: value, pageNumber: 1, size: 1000})
  }

  const handleTypeBuilding = (val: any) => {
    let value = val ? val.value : ''
    console.log(value)
  }

  const handleModuleChange = (val: any) => {
    let Nropisos = val?.NroPisos || 1
    let value = val ? val.value : ''
    setValue('ModuloId', value)
    setError('ModuloId', {})
    setValue('PisoId', '')
    setError('PisoId', {})
    clearFloors()
    getFloors(Nropisos)
  }

  const handleFloorChange = (val: any) => {
    let value = val ? val.value : ''
    setValue('PisoId', value)
    setError('PisoId', {})
  }

  const getTypeBuildings = async () => {
    const data = await getMasterTable('TipoEdificacion', true)
    setTypeBuildings(data)
  }

  const onInvalid = (errors: any) => {
    console.log(errors)
  }

  const onValid = async () => {
    try {
      if (hasChanges) {
        Swal.fire({
          title: '¿Estás seguro?',
          text: `Quieres guardar tus cambios`,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Si, guardar',
          confirmButtonColor: 'var(--kt-primary)',
          cancelButtonText: 'No',
          showLoaderOnConfirm: true,
          allowOutsideClick: () => !Swal.isLoading(),
        })
          .then(async (result) => {
            if (result.isConfirmed) {
              await handleSave(false)
              Swal.close()
            }
          })
          .catch((error) => {
            console.log(error)
          })
          .finally(async () => {
            setSearching(true)
            setLoading(true)
            const filters: CensusFilterModel = {
              CampamentoId: watch('CampamentoId'),
              TipoEdificacionId: watch('TipoEdificacionId'),
              ModuloId: watch('ModuloId'),
              PisoId: watch('PisoId'),
            }
            const {data: response} = await CENSUS_SERVICES.filterCensus(filters)
            if (!response.error && response.data) {
              const parts = response.message.split('|');
              const idCenso = parts[0] || '';
              const estado = parts[1] || '';

              await handleResumen(idCenso)
              setIdCenso(idCenso)
              setEstadoCenso(estado)
              setRooms(response.data)
              setInitialRooms(JSON.parse(JSON.stringify(response.data))) // copia profunda
            }
            setLoading(false)
            setSearching(false)
          })
      } else {
        setSearching(true)
        setLoading(true)
        const filters: CensusFilterModel = {
          CampamentoId: watch('CampamentoId'),
          TipoEdificacionId: watch('TipoEdificacionId'),
          ModuloId: watch('ModuloId'),
          PisoId: watch('PisoId'),
        }
        const {data: response} = await CENSUS_SERVICES.filterCensus(filters)
        const parts = response.message.split('|');
        const idCenso = parts[0] || '';
        const estado = parts[1] || '';
        if(estado === "FIN") {
          Swal.fire({
            title: '¡Censo finalizado!',
            text: `Quieres continuar con el censo?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Si, continuar',
            confirmButtonColor: 'var(--kt-primary)',
            cancelButtonText: 'No',
            showLoaderOnConfirm: true,
            allowOutsideClick: () => !Swal.isLoading(),
          }).then(async (result) => {
            if (result.isConfirmed) {
              if (!response.error && response.data) {
                await handleResumen(idCenso)
                setIdCenso(idCenso)
                setEstadoCenso(estado)
                setRooms(response.data)
                setInitialRooms(JSON.parse(JSON.stringify(response.data))) // copia profunda
              } else {
                setIdCenso(idCenso)
                setEstadoCenso(estado)
                setRooms(response.data)
                setInitialRooms(JSON.parse(JSON.stringify(response.data))) // copia profunda
              }
            }
          }).catch((error) => {
            console.log(error)
          }).finally(async () => {
            Swal.close()
          })
        } else {
          await handleResumen(idCenso)
          setIdCenso(idCenso)
          setEstadoCenso(estado)
          setRooms(response.data)
          setInitialRooms(JSON.parse(JSON.stringify(response.data))) // copia profunda
        }
      }
    } catch (error) {
      console.error('Error al buscar data:', error)
    } finally {
      setSearching(false)
      setLoading(false)
    }
  }

  const handleSearch = handleSubmit(onValid, onInvalid)

  const handleResumen = async (cabeceraId: string) => {
    try {
      setSearching(true)
      setLoading(true)
      const {data: response} = await CENSUS_SERVICES.resumenCensus(cabeceraId)
      if (!response.error && response.data) {
        setSummary(response.data)
        console.log('data => ', response.data)
      }
    } catch (error) {
      console.error('Error al buscar data:', error)
    } finally {
      setSearching(false)
      setLoading(false)
    }
  }

  const handleClear = () => {
    setValue('CampamentoId', '')
    setError('CampamentoId', {})
    // clearCampaments()
    setValue('ModuloId', '')
    setError('ModuloId', {})
    clearModules()
    setValue('PisoId', '')
    setError('PisoId', {})
    clearFloors()
  }

  return (
    <div className='w-100'>
      <div className='pb-10 pb-lg-15'>
        <div className='d-flex row mb-4'>
          <div className='mb-2 col-sm-12 '>
            <label className='required form-label'>Campamento</label>
            <Select
              data={campaments}
              name='CampamentoId'
              onChangeValue={handleCampamentChange}
              control={control}
              errors={errors}
            />
          </div>
          <div className='mb-2 col-sm-12 '>
            <label className='required form-label'>Tipo de Edificación</label>
            <Select
              data={typeBuildings}
              name='TipoEdificacionId'
              onChangeValue={handleTypeBuilding}
              control={control}
              errors={errors}
            />
          </div>
          <div className='mb-2 col-sm-12'>
            <label className='required form-label'>Modulo</label>
            <Select
              name='ModuloId'
              data={modules}
              onChangeValue={handleModuleChange}
              errors={errors}
              control={control}
            />
          </div>
          <div className='mb-2 col-sm-12'>
            <label className='required form-label'>Piso</label>
            <Select
              name='PisoId'
              data={floors}
              onChangeValue={handleFloorChange}
              errors={errors}
              control={control}
            />
          </div>
        </div>
        <div className='mb-4 col-12 d-flex justify-content-between align-items-end'>
          <div className='d-flex gap-2'>
            <button
              type='button'
              className='btn btn-sm btn-primary'
              onClick={handleSearch}
              disabled={searching}
            >
              {!searching && <span className='indicator-label'>Buscar</span>}
              {searching && (
                <span className='indicator-progress d-flex align-items-center'>
                  Buscando...
                  <span className='spinner-border spinner-border-sm ms-2'></span>
                </span>
              )}
            </button>
            <button
              type='button'
              className='btn btn-sm btn-light'
              onClick={handleClear}
              disabled={searching}
            >
              Limpiar
            </button>
          </div>
          {(rooms ?? []).length > 0 && (
            <div
              className='position-fixed d-flex flex-column gap-2'
              style={{
                bottom: '10px',
                left: '10px',
                zIndex: 1050,
              }}
            >
              <div className='alert alert-warning py-2 px-3 mb-0'>
                <>
                  <div className='text-dark fw-semibold'>
                    Camas tendidas: {summary?.CamasTendidas}
                  </div>
                  <div className='text-dark fw-semibold'>
                    Camas destendidas: {summary?.CamasDestendidas}
                  </div>
                  <div className='text-dark fw-semibold'>
                    Camas pendientes: {summary?.CamasPendientes}
                  </div>
                  <div className='text-dark fw-semibold'>
                    Camas totales: {summary?.CamasTotales}
                  </div>
                </>
              </div>

              <div className='d-flex gap-2'>
                <button
                  type='button'
                  className='btn btn-sm btn-success'
                  onClick={() => handleSave(false)}
                  disabled={loadingSave}
                >
                  Guardar
                </button>

                <button
                  type='button'
                  className='btn btn-sm btn-primary'
                  onClick={() => handleSave(true)}
                  disabled={loadingSave}
                >
                  Finalizar
                </button>
              </div>
            </div>
          )}
        </div>
        <div>
          {(rooms ?? []).map((room) => (
            <div
              key={room.HabitacionId}
              className='card border bg-light card-xl-stretch min-w-350px mb-5'
            >
              {/* HEADER HABITACION */}
              <div
                className='card-header border-0 py-5 gap-4 cursor-pointer'
                onClick={() =>
                  handleTap(
                    () => updateRoomBeds(room.HabitacionId, 'TEN'),
                    () => updateRoomBeds(room.HabitacionId, 'DES'),
                  )
                }
              >
                <h3 className='card-title align-items-start flex-column'>
                  <span className='card-label fw-bold fs-3 mb-1'>{room.Codigo}</span>
                </h3>

                <div className='card-toolbar'>
                  <BoundingBox className='me-2 text-muted mt-2' size={32} weight='duotone' />
                </div>
              </div>

              {/* CAMAS */}
              <div className='card-body bg-body'>
                <div className={style.bedsLayout}>
                  {room.Detalle.map((bed) => (
                    <div
                      key={bed.CamaId}
                      className={clsx(style.bedItem, getBedColor(bed.Estado))}
                      onClick={(e) => {
                        e.stopPropagation()

                        handleTap(
                          () => updateBed(room.HabitacionId, bed.CamaId, 'TEN'),
                          () => updateBed(room.HabitacionId, bed.CamaId, 'DES'),
                        )
                      }}
                    >
                      <Bed size={22} weight='duotone' />
                      <span>{bed.Codigo}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* <pre>{JSON.stringify(watch(), null, 2)}</pre> */}
      <div className='d-flex flex-stack pt-15 justify-content-between'>
        <button
          type='button'
          className='btn btn-sm btn-light-primary me-3'
          data-kt-stepper-action='previous'
          onClick={handlePrev}
        >
          Atras
        </button>
        <button
          type='button'
          className='btn btn-sm btn-primary me-3'
          onClick={handleNext}
          //   disabled={searching}
        >
          Proceso de Limpieza
          {/* {!searching && <span className='indicator-label'>Buscar Reserva</span>}
          {searching && (
            <span className='indicator-progress' style={{display: 'block'}}>
              Buscando...
              <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
            </span>
          )} */}
        </button>
      </div>
    </div>
  )
}
